import React, { useEffect, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";

import {
  editUser,
  editUserPhone,
  editUserEmail,
  editUserRole,
  editUserRoleAtAllSites,
  editUserExpiration,
  sendUserActivation,
  refreshUserPin,
  fetchUserPin,
  fetchUserLoginAttempts,
  fetchUserPhoneDetails,
  unfreezeUserAcount
} from "actions/Users";
import { fetchRoleList } from "actions/Roles";
import { fetchLoginTypes } from "actions/Settings";
import { submitNotificationPerformance } from "actions/App";
import { forgotUserPassword } from "actions/Auth";
import { dateTimeFormatter } from "utilities";
import PopoutDropdown from "components/PopoutDropdown";
import DateInput from "components/DateInput";
import TextInput from "components/TextInput";
import SelectInput from "components/SelectInput";
import ButtonContained from "components/ButtonContained";
import LoadingIndicator from "components/LoadingIndicator";

const UserSettingsInformationModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useSelector(state => state.users.userObject);
  const userPin = useSelector(state => state.users.userPin);
  const loginAttempts = useSelector(state => state.users.loginAttempts);
  const userPhoneDetails = useSelector(state => state.users.userPhoneDetails);
  const authUser = useSelector(state => state.auth.authUser);
  const userTimezone = useSelector(state => state.app.userTimezone);
  const site = useSelector(state => state.auth.site);
  const rolesList = useSelector(state => state.roles.rolesList);
  const [localUser, setLocalUser] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewPin, setViewPin] = useState(false);

  useEffect(() => {
    authUser?.permissions?.includes("manage_roles") && dispatch(fetchRoleList());
    authUser?.permissions?.includes("manage_users") && dispatch(fetchLoginTypes());
  }, [authUser])

  useEffect(() => {
    if (!user) {
      setLocalUser(null);
    } else {
      setLocalUser(user);
      authUser?.permissions?.includes("support_view_login_attempts") && dispatch(fetchUserLoginAttempts(user.uuid));
      (authUser?.permissions?.includes("support_view_all_companies") ||
        (authUser?.permissions?.includes("view_phone_details") && authUser?.featureflagAssignments?.includes("device_app_details"))) && dispatch(fetchUserPhoneDetails(user.uuid));
    }
  }, [user]);

  useEffect(() => {
    if (localUser && user) {
      const isChanged = JSON.stringify(localUser) !== JSON.stringify(user);
      setHasChanges(isChanged);
    }
  }, [localUser]);

  const filteredRoles = useMemo(() => {
    if (!rolesList) return [];
  
    // Check if the email belongs to specific domains
    const isInternalEmail =
      user?.email?.includes("@januseurope.com") ||
      user?.email?.includes("@ptiaustralasia.com") ||
      user?.email?.includes("@noke.com") ||
      user?.email?.includes("@janusintl.com");
  
    // If internal email, return all roles
    if (isInternalEmail) {
      return rolesList;
    }
  
    // Otherwise, filter out roles with "support" permissions
    return rolesList.filter(
      (role) =>
        !role.permissions.some((permission) => permission.includes("support"))
    );
  }, [rolesList, user?.email]);

  const handleInputChange = (field, value) => {
    setLocalUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
  };

  const handleEditUser = () => {
    if (localUser.firstName !== user.firstName || localUser.lastName !== user.lastName || localUser.loginType !== user.loginType) {
      dispatch(editUser(
        localUser.uuid,
        localUser.firstName,
        localUser.lastName,
        localUser.loginType,
        localUser.email,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }
    if (localUser.phone !== user.phone) {
      dispatch(editUserPhone(
        localUser.uuid,
        localUser.phone,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
          failedMessage: intl.formatMessage({ id: "notifications.error.failed" }),
          refetch: true
        }
      ));
    }
    if (localUser.roleAtAllSites !== user.roleAtAllSites) {
      dispatch(editUserRoleAtAllSites(
        localUser.uuid,
        localUser.roleAtAllSites,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }
    if (localUser.role.uuid !== user.role.uuid) {
      dispatch(editUserRole(
        localUser.uuid,
        localUser.role,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }
    if (localUser.email !== user.email) {
      dispatch(editUserEmail(
        localUser.uuid,
        localUser.email,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }
    const hasExpiration = (value) => !!value && moment(value).isAfter('2000-01-01');
    const expirationChanged = hasExpiration(localUser.expiration) !== hasExpiration(user.expiration) ||
      (hasExpiration(localUser.expiration) && !moment(localUser.expiration).isSame(user.expiration, 'day'));
    if (expirationChanged) {
      dispatch(editUserExpiration(
        localUser.id,
        localUser.expiration,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }
    /*if (localUser.preUnlockEnabled !== user.preUnlockEnabled) {
      dispatch(editUserKeyUse(
        localUser.uuid,
        localUser.preUnlockEnabled,
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" })
        }
      ));
    }*/
  }

  const handleResetPasswordEmail = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: localUser.id,
      notification_type: "password_reset",
      notification_name: "password_reset",
      start_time: moment().utc()
    }));
    dispatch(forgotUserPassword(
      {
        companyUUID: site.companyUUID,
        email: localUser.email
      },
      {
        successMessage: intl.formatMessage({ id: "notifications.success.sent" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  const handleResetPasswordPhone = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: localUser.id,
      notification_type: "password_reset",
      notification_name: "password_reset",
      start_time: moment().utc()
    }));
    dispatch(forgotUserPassword(
      {
        companyUUID: site.companyUUID,
        phone: localUser.phone
      },
      {
        successMessage: intl.formatMessage({ id: "notifications.success.sentText" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  const handleActivateUser = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: localUser.id,
      notification_type: "account_activation",
      notification_name: "new_account",
      start_time: moment().utc()
    }));
    dispatch(sendUserActivation(
      localUser.uuid,
      false,
      {
        successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  const roleMap = filteredRoles && Object.fromEntries(filteredRoles.map(role => [role.uuid, role]));

  return (
    <div className="min-h-[300px] max-h-[70vh] px-10 pt-4 flex flex-col justify-between gap-8 overflow-scroll custom-scrollbar">
      <div>
        <h5 className="tw-heading-5">{intl.formatMessage({ id: "pages.settings.info" })}</h5>
      </div>
      {localUser ? <>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4 py-2 pl-[1px] pr-[1px]">
            <TextInput
              id="User-Settings-First-Name-Input"
              dataCy="User-Settings-First-Name-Input"
              placeholder={intl.formatMessage({id: "form.firstName"})}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              value={localUser?.firstName ?? ""}
            />
            <TextInput
              id="User-Settings-Last-Name-Input"
              dataCy="User-Settings-Last-Name-Input"
              placeholder={intl.formatMessage({id: "form.lastName"})}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              value={localUser?.lastName ?? ""}
            />
            {(authUser?.permissions?.includes("edit_email_or_phone") ||
              localUser?.role?.tier !== 0 ||
              (localUser?.role?.tier === 0 && site?.pms === "")) && <TextInput
              id="User-Settings-Email-Input"
              dataCy="User-Settings-Email-Input"
              placeholder={intl.formatMessage({id: "form.email"})}
              onChange={(e) => handleInputChange("email", e.target.value)}
              value={localUser?.email ?? ""}
              isDisabled={localUser?.role?.tier === 0 && site?.mobileOnlyTenantAccounts === "true"}
            />}
            {(authUser?.permissions?.includes("edit_email_or_phone") ||
              authUser?.permissions?.includes("support_change_phone")) && <TextInput
              id="User-Settings-Phone-Input"
              dataCy="User-Settings-Phone-Input"
              placeholder={intl.formatMessage({id: "form.phone"})}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              value={localUser?.phone ?? ""}
              validateInput={(val) => /^\d*$/.test(val)} // only digits
            />}
            {/* should only be able to edit role if they have the permission, it is not for themselves, and it is not for a support user */}
            {authUser?.permissions?.includes("manage_roles") &&
              authUser?.id !== localUser?.id &&
              localUser?.role?.tier !== 1 && <SelectInput
              id="User-Settings-Roles-Input"
              dataCy="User-Settings-Roles-Input"
              value={localUser?.role?.uuid || ""}
              placeholder={intl.formatMessage({ id: "form.role" })}
              onChange={(e) => {
                const selectedUuid = e.target.value;
                const selectedRole = roleMap[selectedUuid];
                handleInputChange("role", selectedRole)
              }}
              options={filteredRoles
                ? filteredRoles?.map(r => ({ name: r.name, value: r.uuid }))
                : []
              }
            />}
            {/* only can edit if: not viewing yourself, user being viewed is employee (above role tier 1), 
              logged in user is support user or has roleAtAllSites, user being viewed is 
              tenant && logged in user has manage_tenants OR user 
              being viewed is over tier 0 && has manage_employees */}
            {authUser?.id !== localUser?.id &&
              localUser?.role?.tier >= 2 &&
              (authUser?.role?.tier === 1 || authUser?.roleAtAllSites) &&
              (localUser?.role?.tier === 0 && authUser?.permissions?.includes("manage_tenants") || 
              (localUser?.role?.tier > 0 && authUser?.permissions?.includes("manage_employees"))) && <SelectInput
              id="User-Settings-Assign-To-All-Input"
              dataCy="User-Settings-Assign-To-All-Input"
              value={localUser?.roleAtAllSites ?? false}
              placeholder={intl.formatMessage({ id: "pages.users.create.assignToAll" })}
              onChange={(e) => handleInputChange("roleAtAllSites", e.target.value === "true")}
              options={[
                { name: intl.formatMessage({ id: "form.off" }), value: false },
                { name: intl.formatMessage({ id: "form.on" }), value: true },
              ]}
            />}
            {site.siteLoginType && site.siteLoginType !== "noke" && loginTypes && <SelectInput
              id="User-Settings-Assign-To-All-Input"
              dataCy="User-Settings-Assign-To-All-Input"
              value={localUser?.loginType || ""}
              placeholder={intl.formatMessage({ id: "form.loginType" })}
              onChange={(e) => handleInputChange("loginType", e.target.value)}
              options={loginTypes
                ? loginTypes?.map(t => ({ name: t.charAt(0).toUpperCase() + t.slice(1), value: t }))
                : []
              }
            />}
            {/*site?.preUnlockEnabled &&
              authUser?.featureflagAssignments?.includes("preunlock_enabled") &&
              localUser?.role?.tier == 0 && <SelectInput
              id="User-Settings-Preunlock-Enabled-Input"
              dataCy="User-Settings-Preunlock-Enabled-Input"
              value={localUser?.preUnlockEnabled ?? false}
              placeholder={intl.formatMessage({ id: "form.preunlockEnabledUser" })}
              onChange={(e) => handleInputChange("preUnlockEnabled", e.target.value)}
              options={[
                { name: intl.formatMessage({ id: "form.off" }), value: false },
                { name: intl.formatMessage({ id: "form.on" }), value: true },
              ]}
            />*/}
            {user?.expiration && moment(user.expiration).isAfter('2000-01-01') && (
              <DateInput
                id="User-Settings-Expiration-Date-Input"
                dataCy="User-Settings-Expiration-Date-Input"
                placeholder={intl.formatMessage({id: "comp.repeatSchedule.expirationDate"})}
                date={localUser?.expiration ? moment(localUser.expiration) : null}
                onDateChange={(date) => handleInputChange("expiration", date)}
                displayFormat={site.sitePreferredDateFormat ? site.sitePreferredDateFormat : "MM/DD/YYYY"}
              />
            )}
          </div>
          <div className="grid grid-cols-2">
            <ButtonContained
              id="User-Settings-Information-Save-Changes-Button"
              dataCy="User-Settings-Information-Save-Changes-Button"
              text={intl.formatMessage({ id: "comp.button.saveChanges" })}
              type="medium"
              disabled={!hasChanges}
              onClick={handleEditUser}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-8" style={{ overflow: "unset" }}>

          {/* Reset Password */}
          {(((authUser?.permissions?.includes("manage_tenants") && localUser?.role?.tier === 0) ||
            (authUser?.permissions?.includes("manage_employees") && localUser?.role?.tier > 0)) &&
            (site?.hideUserPasswordActivate === "false") &&
            (localUser?.loginType === "" || localUser?.loginType === "noke")) && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.reset" })}</h5>
            <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.reset.description" })}</p>
            {localUser?.phone !== "" && localUser?.email !== ""
            ?
            <div className="p-[1px]">
              <PopoutDropdown
                id="User-Settings-Modal-Reset-Password-Dropdown-Button"
                dataCy="User-Settings-Modal-Reset-Password-Dropdown-Button"
                label={intl.formatMessage({ id: "comp.button.recoverPassword" })}
                direction="up"
                align="left"
                buttonIcon={null}
                largeButton={true}
                options={
                  [
                    {
                      value: "email",
                      name: intl.formatMessage({ id: "comp.button.sendEmail" })
                    },
                    {
                      value: "phone",
                      name: intl.formatMessage({ id: "comp.button.sendText" })
                    }
                  ]
                }
                onOptionSelect={(option) => option === "phone" ? handleResetPasswordPhone() : handleResetPasswordEmail()}
              />
            </div>
            :
            <ButtonContained
              id="User-Settings-Modal-Reset-Password-Button"
              dataCy="User-Settings-Modal-Reset-Password-Button"
              text={intl.formatMessage({ id: "comp.button.recoverPassword" })}
              type="default"
              onClick={() => localUser?.phone !== "" ? handleResetPasswordPhone() : handleResetPasswordEmail()}
            />}
          </div>}

          {/* Activate Account */}
          {(((authUser?.permissions?.includes("manage_tenants") && localUser?.role?.tier === 0) ||
            (authUser?.permissions?.includes("manage_employees") && localUser?.role?.tier > 0)) &&
            (site?.hideUserPasswordActivate === "false") &&
            (localUser?.loginType === "" || localUser?.loginType === "noke")) && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.activate" })}</h5>
            <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.activate.description" })}</p>
            <ButtonContained
              id="User-Settings-Modal-Activate-Button"
              dataCy="User-Settings-Modal-Activate-Button"
              text={intl.formatMessage({ id: "comp.button.activate" })}
              type="default"
              onClick={() => handleActivateUser()}
            />
          </div>}

          {/* Refresh Pin */}
          {authUser?.permissions?.includes("support_refresh_pin") && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.pin.refresh" })}</h5>
            <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.pin.refresh.description" })}</p>
            <ButtonContained
              id="User-Settings-Modal-Refresh-Pin-Button"
              dataCy="User-Settings-Modal-Refresh-Pin-Button"
              text={intl.formatMessage({ id: "pages.users.pin.refresh" })}
              type="default"
              onClick={() => dispatch(refreshUserPin(localUser.uuid, false, { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }))}
            />
          </div>}

          {/* Unfreeze Account */}
          {authUser?.permissions?.includes("unfreeze_user") && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.unfreeze" })}</h5>
            <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.unfreeze.description" })}</p>
            <ButtonContained
              id="User-Settings-Modal-Unfreeze-Account-Button"
              dataCy="User-Settings-Modal-Unfreeze-Account-Button"
              text={intl.formatMessage({ id: "comp.button.unfreeze" })}
              type="default"
              onClick={() => dispatch(unfreezeUserAcount(localUser.uuid, false, { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }))}
            />
          </div>}

          {/* View Pin */}
          {authUser?.permissions?.includes("support_view_pins") && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.pin" })}</h5>
            {!viewPin && <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.pin.description" })}</p>}
            {viewPin &&
              <div className="flex flex-col gap-2 ">
                {userPin?.pin && userPin.pin !== "" && <div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.pin.user" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{userPin?.pin}</p>
                  </div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.pin.expire" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{dateTimeFormatter(userPin?.expiration, authUser, site, userTimezone)}</p>
                  </div>
                </div>}
                {userPin?.phonePin && userPin?.phonePin !== "" && <div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "comp.table.header.phone" })} {intl.formatMessage({ id: "form.pin" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{userPin?.phonePin}</p>
                  </div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.pin.expire" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{dateTimeFormatter(userPin?.phoneExpiration, authUser, site, userTimezone)}</p>
                  </div>
                </div>}
                {userPin?.emailPin && userPin?.emailPin !== "" && <div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "comp.table.header.email" })} {intl.formatMessage({ id: "form.pin" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{userPin?.emailPin}</p>
                  </div>
                  <div className="flex">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.pin.expire" })}:</p>
                    <p className="tw-paragraph-reg ml-2">{dateTimeFormatter(userPin?.emailExpiration, authUser, site, userTimezone)}</p>
                  </div>
                </div>}
              </div>
            }
            {!viewPin && <ButtonContained
              id="User-Settings-Modal-Pin-Open-Button"
              dataCy="User-Settings-Modal-Pin-Open-Button"
              text={intl.formatMessage({ id: "pages.users.pin" })}
              type="default"
              onClick={() => {dispatch(fetchUserPin(localUser.uuid)); setViewPin(true)}}
            />}
            {viewPin && <ButtonContained
              id="User-Settings-Modal-Pin-Close-Button"
              dataCy="User-Settings-Modal-Pin-Close-Button"
              text={intl.formatMessage({ id: "comp.button.close" })}
              type="default"
              onClick={() => setViewPin(false)}
            />}
          </div>}

          {/* Login Attempts */}
          {authUser?.permissions?.includes("support_view_login_attempts") && <div className="flex flex-col justify-between gap-4">
            <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.login.attempts" })}</h5>
            <p className="tw-paragraph-reg">{intl.formatMessage({ id: "pages.users.login.attempts.description" })}</p>
            {loginAttempts && <div>
              <div className="flex">
                <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.login.attempts" })}:</p>
                <p className="tw-paragraph-reg ml-2">{loginAttempts?.login_attempts}</p>
              </div>
              <div className="flex">
                <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "pages.users.login.frozen" })}:</p>
                <p className="tw-paragraph-reg ml-2">{dateTimeFormatter(loginAttempts?.freeze_expiration, authUser, site, userTimezone)}</p>
              </div>
            </div>}
          </div>}

        </div>

        {/* Phone Details */}
        {localUser && 
          (authUser?.permissions?.includes("support_view_all_companies") ||
          (authUser?.permissions?.includes("view_phone_details") && authUser?.featureflagAssignments?.includes("device_app_details"))) &&
            <div className="flex flex-col gap-4">
              <h5 className="tw-heading-5 font-semibold">{intl.formatMessage({ id: "pages.users.phone" })}</h5>
              <div>
                {userPhoneDetails && userPhoneDetails !== "blank" ? userPhoneDetails.map(device => <div key={device.id} className="grid grid-cols-3 gap-8">
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.phoneNumber" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{localUser?.phone && localUser?.phone !== "" ? localUser?.phone : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.device" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{device.device && device.device !== "" ? device.device : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.platform" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{device.platform && device.platform !== "" ? device.platform : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.platformVersion" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{device.platformVersion && device.platformVersion !== "" ? device.platformVersion : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.appVersion" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{device.appVersion && device.appVersion !== "" ? device.appVersion : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                  <div className="flex flex-col">
                    <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.build" })}:</p>
                    <hr />
                    <p className="tw-paragraph-reg">{device.build && device.build !== "" ? device.build : intl.formatMessage({ id: "form.na" })}</p>
                  </div>
                </div>)
                : <div className="grid grid-cols-3 gap-8">
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.phoneNumber" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.device" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.platform" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.platformVersion" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.appVersion" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
                <div className="flex flex-col">
                  <p className="tw-paragraph-reg font-semibold">{intl.formatMessage({ id: "form.build" })}:</p>
                  <hr />
                  <p className="tw-paragraph-reg">{intl.formatMessage({ id: "form.na" })}</p>
                </div>
              </div>}
              </div>
            </div>
          }
      </> : <div className="flex-1 flex items-center justify-center">
        <LoadingIndicator />
      </div>}
    </div>
  );
}

export default UserSettingsInformationModal;